/**
 * Interaction registers — the named class-string vocabulary for rail rows,
 * ported from the design system's `showcase/lib/registers.js` (RFC 007) so no
 * file hand-rolls a hover/selection treatment that can drift on hue or
 * timing. Port policy: the design system's `--dur-*` / `control-*` rungs map
 * to this repo's plain Tailwind utilities (`duration-100/150`, `min-h-7` =
 * the 28px row, `min-h-8` = the 32px chrome rung); `ease-out` already carries
 * the house deceleration curve via cohesive-tokens.css.
 */

/** Asymmetric hover timing — 100ms in (the hover rule wins while hovered),
 *  150ms out. Pair with a HOVER_BG rung. */
export const HOVER_T =
  "transition-colors duration-150 ease-out hover:duration-100";

/**
 * The tone ladder — hover steps ONE rung up from the row's ground. `tone`
 * names the ground so the utility says what it does: rows on the card (n-00)
 * step to n-05; rows on an inset ground (n-05) step to n-10; rows on a deep
 * ground (n-10, e.g. the drawer) step to n-20.
 */
export const HOVER_BG = {
  card: "hover:bg-n-05",
  inset: "hover:bg-n-10",
  deep: "hover:bg-n-20",
} as const;
export type RailTone = keyof typeof HOVER_BG;

/** Active/selected row chrome — n-10 ground + n-95 text (never an accent
 *  side-stripe; the canon's active-row rule). */
export const ACTIVE_ROW = "bg-n-10 text-n-95";

/**
 * Warm hover wash. Steps off --n-10 on light and --n-20 on dark: dark's
 * --n-10 is the popover/pane ground itself, so mixing it with transparent
 * over that same ground produced no visible change at all — rows looked
 * un-hoverable in dark while working fine in light.
 */
export const ROW_HOVER =
  "hover:bg-[color-mix(in_oklab,var(--n-10)_60%,transparent)] " +
  "dark:hover:bg-[color-mix(in_oklab,var(--n-20)_60%,transparent)]";

/** Selection chrome (--canvas-selection) — the held-object hue. The BOLD hue
 *  lives only on the select control; a row's selection is a faint same-hue
 *  wash. */
export const SELECT_WASH =
  "bg-[color-mix(in_oklab,var(--canvas-selection)_8%,transparent)]";
export const SELECT_CONTROL_FILL =
  "bg-[color-mix(in_oklab,var(--canvas-selection)_14%,transparent)]";

/** The house focus ring — keyboard-only, inside scrolling containers. */
export const FOCUS_RING =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px]";

/**
 * A section head's hover-revealed edge control (the SEC_CTRL register): a
 * small glyph pinned right of an eyebrow, visible while the head row is
 * hovered, while its menu is open, or while it holds focus. Host the head in
 * a wrapper carrying `group/sec`.
 */
export const SECTION_CTRL =
  "inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-n-50 opacity-0 outline-none transition-[opacity,color] duration-100 ease-out hover:text-n-90 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px] group-hover/sec:opacity-100 data-[state=open]:opacity-100";

/** SECTION_CTRL on a deep ground — adds the bg acknowledgment rung. */
export const SECTION_CTRL_DEEP =
  "inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-n-50 opacity-0 outline-none transition-[opacity,color,background-color] duration-100 ease-out hover:bg-n-20 hover:text-n-90 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px] group-hover/sec:opacity-100 data-[state=open]:opacity-100 data-[state=open]:bg-n-20";

/** The 28px row rung + shared row anatomy (pitch, radius, padding). */
export const ROW_BASE = "flex min-h-7 w-full items-center rounded-md";

/** Row label — sans text-base (14px) on n-80; container heads step to n-90
 *  font-medium. */
export const ROW_LABEL = "flex-1 truncate text-base text-n-80";

/** Trailing mono meta on a row (counts, sizes) — system signal, never a
 *  label. */
export const ROW_META = "shrink-0 font-mono text-2xs text-n-40";

/** The mono eyebrow register — view heads only (derived lenses: Starred,
 *  Chats, Shared with me). Places use sans heads; see the view-vs-container
 *  ontology in specs/left-rail. */
export const EYEBROW =
  "font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50";

/** Per-level child indent (the half-inset: children clear the parent's
 *  label, icons tuck under the parent's tile). */
export const CHILD_INDENT_PX = 12;
